import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/providers/auth-context";
import { apiClient } from "@/lib/api/client";

const EMAIL_SOURCE = "speakmail";

/**
 * Record that an email link landed this person in the app.
 *
 * SpeakMail's click events carry a recipient and a timestamp but NO URL, so "which
 * link did they click" is unanswerable from the ESP. GA4 cannot fill the gap either:
 * over 28 days it attributed zero revenue to email on every revenue event, with
 * (direct) holding 99.6% of value, and saw roughly 23% of the clicks SpeakMail
 * recorded. Scanners, blockers and last-touch on a logged-in purchase flow eat the
 * rest, and none of that is fixable by better tagging.
 *
 * Reading the landing on our own domain sidesteps all of it, and keys the click to a
 * real user so it joins to companyId and from there to revenue.
 *
 * Deliberately NOT part of captureUtmParams in app.tsx: that one is first-touch only
 * (it no-ops once utm_source is in localStorage) because signup attribution depends on
 * the FIRST touch being preserved. This fires on every qualifying landing.
 */
export function useEmailClickCapture(): void {
  const { user } = useAuth();
  const { search, pathname } = useLocation();
  // A click is recorded once per browser session. Without this a re-render, a
  // StrictMode double-mount, or a back-navigation each posts again.
  const seen = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(search);
    if (params.get("utm_source") !== EMAIL_SOURCE) return;

    const campaign = params.get("utm_campaign");
    if (!campaign) return;

    const content = params.get("utm_content") ?? "";
    const key = `${campaign}:${content}`;
    if (seen.current.has(key)) return;
    seen.current.add(key);

    // Fire and forget. Attribution must never surface an error to the user or block
    // the render, and the server answers 204 even for a duplicate.
    void apiClient
      .post("/analytics/email-click", {
        campaign: campaign.slice(0, 120),
        content: content.slice(0, 120),
        medium: (params.get("utm_medium") ?? "").slice(0, 60),
        source: EMAIL_SOURCE,
        // Path only, never the query string: it can carry a verification or reset token.
        path: pathname.slice(0, 200),
      })
      .catch(() => {
        // Allow a retry within the session if the request failed outright.
        seen.current.delete(key);
      });
  }, [user, search, pathname]);
}
